import { mutate } from 'swr';

import axiosInstance from 'src/utils/axios';
import { extractErrorMessage } from 'src/utils/errorHandler';

import { zerreactorBackendEndpoints } from 'src/api/services/zerreactor_backend_definition/zerreactorBackendEndpoints';

import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from './constant';

/** **************************************
 * Sign in
 *************************************** */
export const signInWithPassword = async ({ email, password }, setSession) => {
  try {
    const params = { Email: email, Password: password };

    // Step 1: Login to get tokens
    const loginRes = await axiosInstance.post(zerreactorBackendEndpoints.Auth.login, params);

    // Check if login response has the expected format
    // API returns: { AccessToken: "...", RefreshToken: "..." }
    if (!loginRes.data || !loginRes.data.AccessToken || !loginRes.data.RefreshToken) {
      throw new Error('Invalid login response format from server');
    }

    const { AccessToken, RefreshToken } = loginRes.data;

    // Step 2: Set session with tokens
    setSession(AccessToken, RefreshToken);

    // Step 3: Get user profile data using the new token
    try {
      const profileRes = await axiosInstance.get(zerreactorBackendEndpoints.Auth.profile);
      
      // Check if profile response has user data
      // API returns: { Id: 1, FirstName: "...", LastName: "...", ... }
      if (profileRes.data && profileRes.data.Id && profileRes.data.FirstName) {
        const userData = profileRes.data;
        
        // Save user data to localStorage
        localStorage.setItem('userData', JSON.stringify(userData));
        
        return { user: userData, accessToken: AccessToken, refreshToken: RefreshToken };
      }
      
      throw new Error('Invalid profile response format from server');
    } catch (profileError) {
      console.error('Error fetching user profile:', profileError);
      // Even if profile fetch fails, we still have tokens, so we can proceed
      // But we should clear the session since we don't have complete user data
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      delete axiosInstance.defaults.headers.common.Authorization;
      
      throw new Error('Failed to fetch user profile data. Please try again.');
    }

  } catch (error) {
    console.error('Error during sign in:', error);
    
    // Use the new localized error handling system
    const localizedErrorMessage = extractErrorMessage(error);
    throw new Error(localizedErrorMessage);
  }
};


/** **************************************
 * Sign out
 *************************************** */
export const signOut = async () => {
  try {
    // Clear auth tokens and user data
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('userData');
    delete axiosInstance.defaults.headers.common.Authorization;
    
    // Clear virtual login session data
    localStorage.removeItem('virtual_session');
    localStorage.removeItem('admin_backup');
    
    // Clear SWR cache to prevent showing previous user's data
    try {
      // Clear profile endpoint cache specifically
      await mutate(zerreactorBackendEndpoints.Auth.profile, undefined, { revalidate: false });
      
      // Clear other critical auth-related endpoints
      await mutate('/api/Auth/Profile', undefined, { revalidate: false });
    } catch (cacheError) {
      // Don't throw here - logout should still proceed
    }
    
    // Clear login notification modal flag so it re-appears on next login
    sessionStorage.removeItem('login_notification_modal_shown');

    // Notify virtual login context to clear state
    window.dispatchEvent(new CustomEvent('virtualLoginSignOut'));
  } catch (error) {
    console.error('Error during sign out:', error);
    throw error;
  }
};


export const refreshToken = async (setSession) => {
  try {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!accessToken || !refreshTokenValue) {
      throw new Error('No tokens available for refresh');
    }

    const res = await axiosInstance.post(zerreactorBackendEndpoints.Auth.refreshTokens, {
      AccessToken: accessToken,
      RefreshToken: refreshTokenValue,
    });

    // Check if refresh response has the expected format
    // API might return: { AccessToken: "...", RefreshToken: "..." } or { data: { AccessToken: "...", RefreshToken: "..." } }
    let newAccessToken;
    let newRefreshToken;
    
    if (res.data && res.data.AccessToken && res.data.RefreshToken) {
      // Direct format: { AccessToken: "...", RefreshToken: "..." }
      newAccessToken = res.data.AccessToken;
      newRefreshToken = res.data.RefreshToken;
    } else if (res.data && res.data.data && res.data.data.AccessToken && res.data.data.RefreshToken) {
      // Wrapped format: { data: { AccessToken: "...", RefreshToken: "..." } }
      newAccessToken = res.data.data.AccessToken;
      newRefreshToken = res.data.data.RefreshToken;
    } else {
      throw new Error('Failed to refresh tokens - invalid response format');
    }
    
    setSession(newAccessToken, newRefreshToken);
    return { accessToken: newAccessToken, refreshToken: newRefreshToken };
  } catch (error) {
    console.error('Error refreshing token:', error);
    // Use the new localized error handling system for refresh errors too
    const localizedErrorMessage = extractErrorMessage(error);
    // If refresh fails, sign out the user
    await signOut();
    throw new Error(localizedErrorMessage);
  }
};

